import { useTranslation } from 'react-i18next';
import { formatAmount } from '@/domain/player-balance';
import { cn } from '@/lib/cn';
import { Spinner } from '../common/spinner';

export type CashoutControlState = 'available' | 'pending' | 'confirm' | 'unavailable';

export interface CashoutControlProps {
  state: CashoutControlState;
  amount: number | null;
  currency?: string;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

function withCurrency(amount: string, currency: string): string {
  return currency ? `${currency}${amount}` : amount;
}

/**
 * Figma CashoutControl — Available / Pending / Confirm / Unavailable.
 * Amount is never conveyed by color alone.
 */
export function CashoutControl({
  state,
  amount,
  currency = '',
  disabled = false,
  onClick,
  className,
}: CashoutControlProps) {
  const { t } = useTranslation();
  const isConfirm = state === 'confirm';
  const isPending = state === 'pending';
  const isUnavailable = state === 'unavailable';
  const amountLabel =
    isUnavailable || amount == null || !Number.isFinite(amount)
      ? '—'
      : withCurrency(formatAmount(amount), currency);

  return (
    <button
      type="button"
      disabled={disabled || isUnavailable || isPending}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className={cn(
        'flex h-[52px] min-w-0 flex-1 items-center justify-between rounded-md px-4 py-3 text-left',
        isConfirm
          ? 'bg-status-success text-text-on-action'
          : 'border border-solid bg-bg-surface',
        !isConfirm &&
          (isUnavailable ? 'border-border-subtle' : 'border-status-success'),
        (disabled || isUnavailable || isPending) && 'cursor-not-allowed',
        className,
      )}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            'text-[12px] leading-[18px]',
            isConfirm ? 'text-text-on-action' : 'text-text-secondary',
          )}
        >
          {isPending
            ? t('Updating', { defaultValue: 'Updating…' })
            : isConfirm
              ? t('CashoutConfirm', { defaultValue: 'Tap to confirm' })
              : t('Cashout', { defaultValue: 'Cash out' })}
        </span>
        <span
          className={cn(
            'text-[15px] leading-[18px] font-bold',
            isConfirm
              ? 'text-text-on-action'
              : isUnavailable
                ? 'text-text-disabled'
                : 'text-text-primary',
          )}
        >
          {amountLabel}
        </span>
      </span>

      {isPending ? (
        <span className="flex shrink-0 items-center gap-2">
          <Spinner size="sm" label={null} />
          <span className="text-[13px] font-bold text-text-secondary whitespace-nowrap">
            {t('PleaseWait', { defaultValue: 'Please wait' })}
          </span>
        </span>
      ) : (
        <span
          className={cn(
            'shrink-0 text-[13px] font-bold whitespace-nowrap',
            isConfirm
              ? 'text-text-on-action'
              : isUnavailable
                ? 'text-text-disabled'
                : 'text-status-success',
          )}
        >
          {isConfirm
            ? t('Confirm', { defaultValue: 'Confirm' })
            : isUnavailable
              ? t('Unavailable', { defaultValue: 'Unavailable' })
              : t('Cashout', { defaultValue: 'Cash out' })}
        </span>
      )}
    </button>
  );
}
